import html2pdf from "html2pdf.js";
import { jsPDF } from "jspdf";

// Genera un PDF de UNA sola hoja carta, escalando el contenido para que quepa
// completo dentro de los márgenes (usado para la Lista de Precios).
async function _buildSinglePagePdf(element) {
  const prevW = element.style.width, prevMW = element.style.maxWidth;
  element.style.width = "750px"; element.style.maxWidth = "750px";
  element.classList.add("pdf-exporting");
  try {
    const canvas = await html2pdf()
      .set({ html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", windowWidth: 750 } })
      .from(element).toCanvas().get("canvas");
    const pageW = 215.9, pageH = 279.4, mx = 12, my = 12;
    const availW = pageW - mx * 2, availH = pageH - my * 2;
    const ratio = canvas.height / canvas.width;
    let imgW = availW, imgH = availW * ratio;
    if (imgH > availH) { imgH = availH; imgW = availH / ratio; }
    const offsetX = mx + (availW - imgW) / 2;
    const pdf = new jsPDF({ unit: "mm", format: "letter", orientation: "portrait" });
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.98), "JPEG", offsetX, my, imgW, imgH);
    return pdf;
  } finally {
    element.style.width = prevW; element.style.maxWidth = prevMW;
    element.classList.remove("pdf-exporting");
  }
}

export async function downloadSinglePagePdf(element, filename = "documento") {
  if (!element) return;
  const pdf = await _buildSinglePagePdf(element);
  pdf.save(`${filename}.pdf`);
}

// Devuelve el PDF (1 hoja carta) como Blob, para subirlo como documento.
export async function buildSinglePageBlob(element) {
  if (!element) return null;
  const pdf = await _buildSinglePagePdf(element);
  return pdf.output("blob");
}

export async function printSinglePagePdf(element) {
  if (!element) return;
  const pdf = await _buildSinglePagePdf(element);
  pdf.autoPrint();
  window.open(pdf.output("bloburl"), "_blank");
}

// Genera y descarga un PDF (hoja carta) a partir de un nodo del DOM.
// Fuerza temporalmente el ancho a tamaño carta (816px ≈ 215.9mm @96dpi) para que
// el PDF salga con el mismo formato que la vista de impresión, no con el ancho de pantalla.
export function downloadPdf(element, filename = "documento") {
  if (!element) return;
  const prevWidth = element.style.width;
  const prevMaxWidth = element.style.maxWidth;
  element.style.width = "750px";
  element.style.maxWidth = "750px";
  element.classList.add("pdf-exporting");
  const restore = () => {
    element.style.width = prevWidth;
    element.style.maxWidth = prevMaxWidth;
    element.classList.remove("pdf-exporting");
  };
  const opt = {
    margin: [10, 8, 10, 8],
    filename: `${filename}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", windowWidth: 750 },
    jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
    pagebreak: { mode: ["css", "legacy"], avoid: ["tr", ".avoid-break"] },
  };
  return html2pdf().set(opt).from(element).save().then(restore).catch(restore);
}

// Construye el jsPDF (carta) repitiendo el membrete en cada hoja y con pie "Hoja X de N".
async function _buildCotizacionPdf(bodyEl, headerEl) {
  const forceW = (el) => {
    el.dataset._w = el.style.width; el.dataset._mw = el.style.maxWidth;
    el.style.width = "750px"; el.style.maxWidth = "750px";
  };
  const restoreW = (el) => { el.style.width = el.dataset._w || ""; el.style.maxWidth = el.dataset._mw || ""; };
  forceW(bodyEl); forceW(headerEl);
  bodyEl.classList.add("pdf-exporting");
  const h2c = { scale: 2, useCORS: true, backgroundColor: "#ffffff", windowWidth: 750 };
  try {
    const headerCanvas = await html2pdf().set({ html2canvas: h2c }).from(headerEl).toCanvas().get("canvas");
    const headerImg = headerCanvas.toDataURL("image/png");
    const ratio = headerCanvas.height / headerCanvas.width;
    const pageW = 215.9, pageH = 279.4, mx = 8, topGap = 6, footerGap = 6;
    const headerWmm = pageW - mx * 2;
    const headerHmm = headerWmm * ratio;
    const topMargin = topGap + headerHmm + 4;
    const worker = html2pdf().set({
      margin: [topMargin, mx, footerGap + 6, mx],
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: h2c,
      jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"], avoid: ["tr", ".avoid-break"] },
    }).from(bodyEl).toPdf();
    const pdf = await worker.get("pdf");
    const total = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      pdf.setPage(i);
      pdf.addImage(headerImg, "PNG", mx, topGap, headerWmm, headerHmm);
      pdf.setFontSize(9);
      pdf.setTextColor(110);
      pdf.text(`Hoja ${i} de ${total}`, pageW - mx, pageH - footerGap, { align: "right" });
    }
    return pdf;
  } finally {
    bodyEl.classList.remove("pdf-exporting");
    restoreW(bodyEl); restoreW(headerEl);
  }
}

// Descarga el PDF de la cotización (membrete en cada hoja + pie "Hoja X de N").
export async function downloadCotizacionPdf(bodyEl, headerEl, filename = "documento") {
  if (!bodyEl || !headerEl) return;
  const pdf = await _buildCotizacionPdf(bodyEl, headerEl);
  pdf.save(`${filename}.pdf`);
}

// Devuelve el PDF de la cotización (membrete en cada hoja) como Blob, para compartir.
export async function buildCotizacionBlob(bodyEl, headerEl) {
  if (!bodyEl || !headerEl) return null;
  const pdf = await _buildCotizacionPdf(bodyEl, headerEl);
  return pdf.output("blob");
}

// Genera el mismo PDF formateado y abre el diálogo de impresión.
export async function printCotizacionPdf(bodyEl, headerEl, filename) {
  if (!bodyEl || !headerEl) return;
  const pdf = await _buildCotizacionPdf(bodyEl, headerEl);
  if (filename) pdf.setProperties({ title: filename });
  pdf.autoPrint();
  const url = pdf.output("bloburl");
  window.open(url, "_blank");
}
